import {
    faBell,
    faCalendarAlt,
    faCashRegister,
    faCode,
    faCreditCard,
    faDollarSign,
    faEnvelope,
    faLandmark,
    faPlus,
    faReceipt,
    faStepForward,
    faSync,
    faUsers,
    IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useCurrency from '@hooks/useCurrency';
import { Badge, Progress, Tooltip } from '@ui';
import moment from 'moment';
import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentStatusComponent, renderPaymentStatus } from '../../MollieConnect/PaymentStatusComponent';
import { formatInvoiceMoment } from '../i18n';
import { UserInvoice } from '../types';
import { PriceComponent } from './PriceComponent';
import PriceDataGrid from './PriceDataGrid';
import RatePaymentComponent from './RatePaymentComponent';
import { BaseComponentProps } from './types';

interface Props extends BaseComponentProps {}

type StatusIconType = 'error' | 'success' | 'warning';

/** Maps the semantic type of a status icon to its text colour. */
const statusIconColor = (type?: StatusIconType) => {
    switch (type) {
        case 'error':
            return 'tw-text-error';

        case 'warning':
            return 'tw-text-warning';

        case 'success':
            return 'tw-text-success';

        default:
            return undefined;
    }
};

/** Middle column of an invoice row: status icons, dunnings, payment progress, dates and price badge. */
export const MetaComponent = React.memo(({ invoice }: Props) => {
    const { t, i18n } = useTranslation('invoice');
    const { getSymbol } = useCurrency();
    // Localised date/time output, "-" when the date is missing.
    const formatDate = (date: string | null | undefined) =>
        date ? formatInvoiceMoment(date, i18n.resolvedLanguage, 'L LT') : '-';
    // Collects all indicator icons of the row, each with its tooltip and semantic colour.
    const statusIcons = useMemo(() => {
        const icons: { icon: IconDefinition; tooltip: ReactNode; type?: StatusIconType }[] = [];

        if (invoice.iban && !invoice.bic) {
            icons.push({ icon: faLandmark, type: 'error', tooltip: t('invoiceList.meta.ibanNotValidated') });
        }

        // Online (Mollie) payment: shown as a plain icon so the price card keeps its full width.
        if (invoice.online_payment) {
            const status = renderPaymentStatus(invoice.online_payment.status);
            icons.push({
                icon: faCreditCard,
                type: 'danger' === status ? 'error' : 'light' === status ? undefined : status,
                tooltip: (
                    <div>
                        <div className="tw-px-2 tw-pt-2 tw-text-sm tw-font-semibold">
                            {t('invoiceList.meta.onlinePayment')}
                        </div>
                        <PaymentStatusComponent paymentId={invoice.online_payment.id} />
                    </div>
                )
            });
        }

        if (invoice.last_datev_export) {
            icons.push({ icon: faCode, type: 'success', tooltip: t('invoiceList.meta.datevExportCreated') });
        }

        if (invoice.reminder_for_id) {
            icons.push({ icon: faBell, tooltip: t('invoiceList.meta.isDunning') });
        }

        if (invoice.isClubMember) {
            icons.push({ icon: faUsers, tooltip: t('invoiceList.meta.recipientIsClubMember') });
        }

        if (!!invoice.rates) {
            icons.push({ icon: faCalendarAlt, tooltip: <RatePaymentComponent rates={invoice.rates} /> });
        }

        if (!invoice.rates && invoice.sepa_downloaded_at) {
            icons.push({ icon: faReceipt, tooltip: t('invoiceList.meta.sepaXmlAlreadyCreated'), type: 'success' });
        } else if (invoice.iban && invoice.bic) {
            icons.push({ icon: faReceipt, tooltip: t('invoiceList.meta.sepaXmlCanBeCreated') });
        }

        if (invoice.due_date ? moment(invoice.due_date).isBefore(moment()) && !invoice.is_paid : false) {
            icons.push({ icon: faDollarSign, type: 'error', tooltip: t('invoiceList.meta.paymentOverdue') });
        }

        return icons;
    }, [invoice, t]);

    // Opens the PDF of a dunning in a new browser tab.
    const handleShowDunning = (dunning: Partial<UserInvoice>) => {
        let popup = window.open(dunning.url, '_blank');
        if (popup != null) popup.focus();
    };

    const paymentProgress: number =
        invoice.payments_sum_amount / Number(invoice.price_data?.total?.replace('.', '').replace(',', '.'));

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex-1">
                {/* Icons align to the bottom of the price card so they sit directly above the progress line. */}
                <div className="tw-flex tw-items-end tw-justify-between">
                    <div className="tw-text-md tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-pb-1 tw-text-accents-7">
                        {statusIcons.length > 0
                            ? statusIcons.map((icon, index) => (
                                  <Tooltip key={index} tooltip={icon.tooltip} className="tw-shrink-0">
                                      <FontAwesomeIcon icon={icon.icon} className={statusIconColor(icon.type)} />
                                  </Tooltip>
                              ))
                            : '-'}
                    </div>
                    <div className="tw-w-[12rem] tw-min-w-fit tw-shrink-0 tw-pl-2">
                        <PriceComponent invoice={invoice} />
                    </div>
                </div>
                {invoice.dunnings?.length > 0 && (
                    <div className="tw-my-2 tw-flex tw-flex-col tw-gap-1">
                        {invoice.dunnings.map((dunning) => (
                            <div key={dunning.id} className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                                <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-gap-x-3 *:tw-text-xs *:tw-font-semibold *:tw-text-accents-7">
                                    <span>{t('invoiceList.meta.dunningLevel', { level: dunning.dunning_level })}</span>
                                    <Tooltip tooltip={t('invoiceList.meta.dunningCreatedAt')} placement="top">
                                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                        {formatDate(dunning.created_at)}
                                    </Tooltip>
                                    <Tooltip tooltip={t('invoiceList.meta.dunningSentAt')} placement="top">
                                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                                        {dunning.send_at
                                            ? formatDate(dunning.send_at)
                                            : t('invoiceList.meta.dunningNotSent')}
                                    </Tooltip>
                                </div>
                                <Tooltip
                                    placement="right"
                                    className="tw-shrink-0"
                                    tooltip={
                                        <PriceDataGrid
                                            price_data={dunning.price_data}
                                            currencyCode={invoice.currency}
                                        />
                                    }
                                >
                                    <Badge
                                        size="xs"
                                        color="error"
                                        flat
                                        className="tw-flex tw-gap-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleShowDunning(dunning);
                                        }}
                                    >
                                        {dunning.price_data?.total ?? '-'}{' '}
                                        {getSymbol(invoice.currency)}
                                    </Badge>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                )}
                <div className="tw-my-2">
                    <Progress
                        color="success"
                        size="xs"
                        progress={
                            paymentProgress > 0 && paymentProgress < 1 ? paymentProgress : paymentProgress >= 1 ? 1 : 0
                        }
                    />
                </div>
                <div className="tw-flex tw-flex-wrap tw-gap-x-3 *:tw-text-xs *:tw-font-semibold *:tw-text-accents-7">
                    <Tooltip tooltip={t('invoiceList.meta.createdAt')} placement="top">
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        {formatDate(invoice.created_at)}
                    </Tooltip>

                    <Tooltip tooltip={t('invoiceList.meta.updatedAt')} placement="top">
                        <FontAwesomeIcon icon={faSync} className="mr-2" />
                        {formatDate(invoice.updated_at)}
                    </Tooltip>

                    {invoice.due_date && (
                        <Tooltip tooltip={t('invoiceList.meta.dueDate')} placement="top">
                            <FontAwesomeIcon icon={faStepForward} className="mr-2" />
                            {formatDate(invoice.due_date)}
                        </Tooltip>
                    )}

                    {invoice.send_at && (
                        <Tooltip tooltip={t('invoiceList.meta.sentAt')} placement="top">
                            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                            {formatDate(invoice.send_at)}
                        </Tooltip>
                    )}

                    {invoice.sepa_downloaded_at && (
                        <Tooltip tooltip={t('invoiceList.meta.sepaDownloaded')} placement="top">
                            <FontAwesomeIcon icon={faReceipt} className="mr-2" />
                            {formatDate(invoice.sepa_downloaded_at)}
                        </Tooltip>
                    )}

                    {invoice.last_datev_export && (
                        <Tooltip tooltip={t('invoiceList.meta.datevExport')} placement="top">
                            <FontAwesomeIcon icon={faCode} className="mr-2" />
                            {formatDate(invoice.last_datev_export)}
                        </Tooltip>
                    )}

                    {invoice.payed_at && (
                        <Tooltip tooltip={t('invoiceList.meta.paidAt')} placement="top">
                            <FontAwesomeIcon icon={faCashRegister} className="mr-2" />
                            {formatDate(invoice.payed_at)}
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
});

MetaComponent.displayName = 'MetaComponent';
